import React, { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const UNDERLINE_TAB_ATTR = 'data-underline-tab';

export const underlineTabItemClassName = (active: boolean): string =>
    `flex shrink-0 items-center justify-start gap-2 px-1 py-2 rounded-sm focus-visible:outline-brand-accent focus-visible:outline-2 focus-visible:outline-offset-2 ${
        active ? 'text-foreground-default' : 'text-brand-muted hover:cursor-pointer hover:text-foreground-default'
    }`;

export const underlineTabLabelClassName = (active: boolean): string => `justify-start text-base ${active ? 'font-semibold' : 'font-medium'}`;

interface UnderlineTabBarProps {
    id?: string;
    activeKey?: string | null;
    children: ReactNode;
    className?: string;
}

/**
 * Full-width underline tab row: no wrap, horizontal scroll, brand-accent indicator that slides
 * to the active tab. Children must set `data-underline-tab` to their key.
 */
export const UnderlineTabBar = (props: UnderlineTabBarProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

    const hideIndicator = useCallback(() => {
        setIndicator((prev) => (prev.ready ? { left: prev.left, width: prev.width, ready: false } : prev));
    }, []);

    const measure = useCallback(() => {
        const track = trackRef.current;
        const activeKey = props.activeKey;
        if (!track || activeKey == null || activeKey === '') {
            hideIndicator();
            return;
        }

        const selector = `[${UNDERLINE_TAB_ATTR}="${activeKey.replace(/"/g, '\\"')}"]`;
        const active = track.querySelector(selector) as HTMLElement | null;
        if (!active) {
            hideIndicator();
            return;
        }

        const left = active.offsetLeft;
        const width = active.offsetWidth;
        setIndicator((prev) => {
            if (prev.left === left && prev.width === width && prev.ready) return prev;
            return { left, width, ready: true };
        });

        const scroller = scrollRef.current;
        if (!scroller || typeof scroller.scrollTo !== 'function' || scroller.clientWidth <= 0) return;
        const padding = 16;
        if (left < scroller.scrollLeft) {
            scroller.scrollTo({ left: Math.max(0, left - padding), behavior: 'smooth' });
        } else if (left + width > scroller.scrollLeft + scroller.clientWidth) {
            scroller.scrollTo({ left: left + width - scroller.clientWidth + padding, behavior: 'smooth' });
        }
    }, [hideIndicator, props.activeKey]);

    useLayoutEffect(() => {
        measure();
    }, [measure, props.children]);

    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => measure());
        observer.observe(track);
        return () => observer.disconnect();
    }, [measure]);

    return (
        <div
            id={props.id}
            ref={scrollRef}
            className={`border-border-default no-scrollbar w-full overflow-x-auto border-b ${props.className ?? ''}`.trim()}
            role='tablist'>
            <div
                ref={trackRef}
                className='relative inline-flex min-w-full flex-nowrap items-center justify-start gap-4'>
                {props.children}
                <span
                    aria-hidden
                    className={`bg-brand-accent pointer-events-none absolute bottom-0 h-0.5 rounded-full transition-[left,width,opacity] duration-200 ease-out ${
                        indicator.ready ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ left: indicator.left, width: indicator.width }}
                />
            </div>
        </div>
    );
};
